import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { useMentionSearch } from "@/hooks/use-mention-search";
import { useTranslation } from "@/packages/utils";
import { ENTITY_ICONS } from "./entity-icons";
import type { EntityContext } from "@/stores/chat-context-store";

interface MentionPopoverProps {
  open: boolean;
  onSelect: (entity: EntityContext) => void;
  onClose: () => void;
  searchQuery: string;
}

export interface MentionPopoverHandle {
  /** Returns true if the key was handled by the popover */
  handleKey: (key: string) => boolean;
}

export const MentionPopover = forwardRef<
  MentionPopoverHandle,
  MentionPopoverProps
>(function MentionPopover({ open, onSelect, onClose, searchQuery }, ref) {
  const { t } = useTranslation(["chat"]);
  const { sections, flatItems, isLoading } = useMentionSearch(searchQuery);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset active index when results change
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset index when result count changes
  useEffect(() => {
    setActiveIndex(0);
  }, [flatItems.length]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeItem = listRef.current.querySelector(
      `[data-index="${activeIndex}"]`,
    );
    activeItem?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleSelect = useCallback(
    (index: number) => {
      const item = flatItems[index];
      if (!item) return;
      onSelect(item.entity);
    },
    [flatItems, onSelect],
  );

  // Expose keyboard handling to parent
  useImperativeHandle(
    ref,
    () => ({
      handleKey(key: string): boolean {
        if (!open || flatItems.length === 0) return false;

        switch (key) {
          case "ArrowDown":
            setActiveIndex((prev) =>
              prev < flatItems.length - 1 ? prev + 1 : 0,
            );
            return true;
          case "ArrowUp":
            setActiveIndex((prev) =>
              prev > 0 ? prev - 1 : flatItems.length - 1,
            );
            return true;
          case "Enter":
            handleSelect(activeIndex);
            return true;
          case "Escape":
            onClose();
            return true;
          default:
            return false;
        }
      },
    }),
    [open, flatItems.length, activeIndex, handleSelect, onClose],
  );

  const activeId = useMemo(
    () => (flatItems.length > 0 ? `mention-option-${activeIndex}` : undefined),
    [flatItems.length, activeIndex],
  );

  if (!open) return null;

  // Build a flat index counter so keyboard nav works across sections
  let flatIndex = 0;

  return (
    <AnimatePresence>
      <motion.div
        className="absolute bottom-full left-0 mb-2 min-w-[300px] max-h-[280px] overflow-y-auto bg-card rounded-lg border border-border shadow-lg p-1 z-50"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={springs.snappy}
        role="listbox"
        aria-label="Mentions"
        aria-activedescendant={activeId}
        ref={listRef}
      >
        {flatItems.length === 0 ? (
          <div className="px-3 py-2 text-sm text-muted">
            {isLoading
              ? t("chat:mentionPopover.loading")
              : t("chat:mentionPopover.noResults")}
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.label}>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted select-none">
                {section.label}
              </div>
              {section.items.map((item) => {
                const idx = flatIndex++;
                const isActive = idx === activeIndex;
                const Icon = ENTITY_ICONS[item.entity.type];
                return (
                  <button
                    type="button"
                    key={item.entity.id}
                    id={`mention-option-${idx}`}
                    role="option"
                    aria-selected={isActive}
                    data-index={idx}
                    className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-left cursor-pointer transition-colors ${
                      isActive
                        ? "bg-accent/10 text-primary"
                        : "text-primary hover:bg-accent/5"
                    }`}
                    onClick={() => handleSelect(idx)}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <Icon size={16} className="shrink-0 text-muted" />
                    <div className="flex-1 min-w-0">
                      <HighlightedText
                        text={item.entity.label}
                        query={searchQuery}
                      />
                      {item.subtitle ? (
                        <span className="ml-2 inline-block text-[10px] uppercase font-medium text-muted bg-muted/10 px-1.5 py-0.5 rounded">
                          {item.subtitle}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </motion.div>
    </AnimatePresence>
  );
});

function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query) return <span className="text-sm truncate">{text}</span>;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerQuery);

  if (matchIndex === -1)
    return <span className="text-sm truncate">{text}</span>;

  const before = text.slice(0, matchIndex);
  const match = text.slice(matchIndex, matchIndex + query.length);
  const after = text.slice(matchIndex + query.length);

  return (
    <span className="text-sm truncate">
      {before}
      <span className="font-semibold text-accent">{match}</span>
      {after}
    </span>
  );
}
